import React from 'react'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { useTranslation } from 'react-i18next'
import { ActiveCall } from 'src/store/modules/communication/state'
import { getAvatarSx } from 'src/components/common/personaAvatar'

export function getActiveCallLabel(call: ActiveCall, unknownLabel = 'Unknown') {
  const storageObject = call.subscriber?.storageObjects?.[0]
  const primary = storageObject?.primary?.trim()

  return {
    name: primary || unknownLabel,
    phone: call.subscriber?.identifier || '',
    icon: storageObject?.icon || undefined,
    initial: primary ? primary.charAt(0).toUpperCase() : '?',
  }
}

export function formatLiveCallDuration(createdAt: string, now: number) {
  const started = new Date(createdAt).getTime()
  const totalSeconds = Number.isNaN(started)
    ? 0
    : Math.max(0, Math.floor((now - started) / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const paddedSeconds = String(seconds).padStart(2, '0')

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`
  }

  return `${minutes}:${paddedSeconds}`
}

interface ActiveCallsPopoverProps {
  anchorEl: HTMLElement | null
  calls: ActiveCall[]
  onClose: () => void
  onOpenCallHistory: () => void
  handleOpenCallDetails: (call: ActiveCall) => void
}

const ActiveCallsPopover = ({
  anchorEl,
  calls,
  onClose,
  onOpenCallHistory,
  handleOpenCallDetails,
}: ActiveCallsPopoverProps) => {
  const { t } = useTranslation('common')
  const open = Boolean(anchorEl)
  const unknownLabel = t('currentCalls.unknown', 'Unknown')
  const [now, setNow] = React.useState(() => Date.now())

  React.useEffect(() => {
    if (!open) return

    const timer = window.setInterval(() => setNow(Date.now()), 1000)

    return () => window.clearInterval(timer)
  }, [open])

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(event) => event.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 8,
        sx: {
          mt: 2,
          width: 360,
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <Typography
        sx={{
          px: 4,
          pt: 3.5,
          pb: 2,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: 'grey.500',
          textTransform: 'uppercase',
        }}
      >
        {t('currentCalls.title', 'Active calls')}
      </Typography>
      <Stack sx={{ maxHeight: 360, overflowY: 'auto' }}>
        {calls.map((call) => {
          const label = getActiveCallLabel(call, unknownLabel)

          return (
            <Stack
              onClick={() => handleOpenCallDetails(call)}
              key={call.id}
              direction="row"
              alignItems="center"
              columnGap={3}
              sx={{
                cursor: 'pointer',
                px: 4,
                py: 2.5,
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Avatar
                alt={label.name}
                src={label.icon}
                sx={{
                  ...getAvatarSx(36, Boolean(label.icon), Math.round(36 * 0.4)),
                }}
              >
                {label.initial}
              </Avatar>
              <Stack flex={1} minWidth={0}>
                <Typography
                  noWrap
                  fontSize={14}
                  fontWeight={700}
                  lineHeight={1.3}
                  color="grey.900"
                >
                  {label.name}
                </Typography>
                <Typography
                  noWrap
                  fontSize={12}
                  lineHeight={1.35}
                  color="grey.500"
                >
                  {label.phone}
                </Typography>
              </Stack>
              <Typography
                fontSize={13}
                color="grey.600"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  minWidth: 36,
                  textAlign: 'right',
                }}
              >
                {formatLiveCallDuration(call.createdAt, now)}
              </Typography>
            </Stack>
          )
        })}
      </Stack>
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={onOpenCallHistory}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            px: 4,
            py: 3,
            borderRadius: 0,
            fontSize: 14,
            fontWeight: 600,
            color: '#5B4DFF',
            textTransform: 'none',
            '&:hover': { bgcolor: 'grey.50' },
          }}
        >
          {t('currentCalls.openCallHistory', 'Open call history →')}
        </Button>
      </Box>
    </Popover>
  )
}

export default ActiveCallsPopover
